import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {
  Box, Button, ButtonBase, Collapse, IconButton, Modal, Skeleton, Stack, Tooltip, Typography, useMediaQuery, useTheme
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon, ArrowDown01Icon, ArrowLeft01Icon, ArrowRight01Icon, Bug01Icon, Calendar03Icon, Cancel01Icon,
  CustomerSupportIcon, EyeIcon, HeadsetIcon, LanguageSkillIcon, PhoneIcon, Tag01Icon, UserEdit01Icon
} from "@hugeicons/core-free-icons";
import {useAtom} from "jotai";
import {toast} from "react-toastify";
import {SelectedConversationAtom} from "../../../project/dashboard/atoms";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {SignInContext} from "../../../../context/SignInContext";
import {ProjectRoles} from "../../../../context/AppContext";
import {useRegistryProject} from "../../RegistryProjectContext";
import useConfirm from "../../../../hooks/useConfirm";
import useCallConfirm from "../../../../hooks/useCallConfirm";
import {useWebrtc, WEBRTC_ROLES} from "../../../../context/WebrtcContext";
import {SendToGroupButton} from "../../../../components/webrtc/SendToGroupButton";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";
import {useFlowProtection} from "../../../../hooks/useFlowProtection";
import {FlowProtectionButton} from "../../../../components/FlowProtectionButton";
import {SupervisionHistoryItem} from "../../../project/supervision-page/components/supervision-history-item";
import {ContactPhoneNumber} from "../../../project/supervision-page/components/contact-phone-number";
import {AVATAR_COLORS, initialsOf, SenderName, StatusBadge, TagPills, TransferNumber} from "./conversationUi";

const roundButtonSx = {
  width: 34,
  height: 34,
  borderRadius: "50% !important",
  border: "1px solid #ECECF3",
  color: "#4A4A63",
  bgcolor: "#fff",
  "&:hover": {bgcolor: "#F4F4FA"},
  "&.Mui-disabled": {opacity: 0.4, bgcolor: "#fff"},
};

const tintedActionSx = {
  flex: {xs: 1, md: "none"}, py: 1, px: 1.5, borderRadius: "8px", textTransform: "none", fontSize: 13, fontWeight: 600,
  whiteSpace: "nowrap", transition: "background-color .15s ease",
};

const softGreenSx = {color: "#1E7F52", bgcolor: "#D9F2E4", "&:hover": {bgcolor: "#C9EBD8"}};

const navButtonSx = {
  width: {xs: 60, md: "auto"}, height: {xs: 48, md: 40}, px: {xs: 0, md: 2}, flexShrink: 0, borderRadius: "12px",
  gap: {xs: "2px", md: "6px"}, fontSize: {xs: 10, md: 13}, fontWeight: 600, lineHeight: 1,
  transition: "background-color .15s ease",
  "&.Mui-disabled": {color: "#B4B4C3", bgcolor: "#F7F7FB", borderColor: "transparent"},
};

const previousButtonSx = {
  ...navButtonSx, flexDirection: {xs: "column", md: "row"}, color: "#374151", bgcolor: "#fff", border: "1px solid #ECECF3",
  "&:hover": {bgcolor: "#FAFAFD"},
};

const nextButtonSx = {
  ...navButtonSx, flexDirection: {xs: "column-reverse", md: "row"}, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)",
  "&:hover": {bgcolor: "rgba(132, 145, 247, 0.22)"},
};

const InfoItem =({icon, label, children}) => (
  <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
    <Box sx={{
      width: 30, height: 30, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", bgcolor: "#fff", border: "1px solid #ECECF3", color: "#8491F7",
    }}>
      <HugeiconsIcon icon={icon} size={15}/>
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0"}}>{label}</Typography>
      <Box sx={{fontSize: 13, color: "#23264D", fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
        {children}
      </Box>
    </Box>
  </Stack>
);

// Conversation details in the dashboard's style: header with caller, status and actions, an info strip, then the
// messages. flow: Firebase flow the conversation ran on (redirect vdn, phonebook);
// subProjectOf(conversation) / tagsOf(conversation): its sub project and tag names, as shown in the table.
// flowOf(conversation): optional, finds the flow when `flow` isn't given. A conversation opened with `messageId`
// scrolls to that message once the messages are loaded.
export const ConversationModal = ({flow: flowProp, flowOf, subProjectOf, tagsOf, showSupervise = false, navigation}) => {
  const [conversation, setConversation] = useAtom(SelectedConversationAtom);
  const flow = flowProp || (conversation && flowOf ? flowOf(conversation) : null) || null;
  const open = Boolean(conversation);
  const {currentUser} = useContext(SignInContext);
  const {project, role, members} = useRegistryProject();
  const webrtc = useWebrtc();
  // project ADMIN / MANAGER / AGENT call through the browser phone only (it must be Connected), others by VDN
  const useBrowserPhone = WEBRTC_ROLES.includes(role);
  const confirmCall = useCallConfirm("Do you want to initiate conversation");

  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [playerSlot, setPlayerSlot] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [infoOpen, setInfoOpen] = useState(null);
  const infoExpanded = infoOpen ?? !isMobile;
  const prevItem = navigation ? navigation.prev : prev;
  const nextItem = navigation ? navigation.next : next;
  const [openContact, setOpenContact] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const abortRef = useRef(null);

  const flowUid = conversation?.flowUid || flow?.id;
  const [updatedValue] = useStompWebsocket(conversation?.status === "ACTIVE" ? `/topic/session/${conversation?.id}` : null);
  const {token: protectionToken, extend: extendProtection} = useFlowProtection();

  const confirmMonitoring = useConfirm(
    `This conversation is already being monitored by ${conversation?.monitoredBy || "someone"}`,
    `Are you sure you want to take over monitoring from ${conversation?.monitoredBy || "someone"}?`
  );

  const fetchMessages = useCallback(() => {
    if (!conversation?.id) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setMessagesLoading(true);
    ConversationRepository.getMessages(conversation.id, controller.signal)
      .then(response => {
        setMessages([...(response?.data || [])].sort((a, b) => a.id - b.id));
        if (protectionToken) extendProtection();
      })
      .catch(error => console.log(error))
      .finally(() => !controller.signal.aborted && setMessagesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, flowUid, protectionToken]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages, updatedValue]);

  // Protected messages unlocked (or locked again): reload the open conversation, its sender comes unmasked.
  // Its messages reload through fetchMessages.
  const tokenRef = useRef(protectionToken);
  useEffect(() => {
    if (tokenRef.current === protectionToken) return;
    tokenRef.current = protectionToken;
    if (!conversation?.id) return;
    const id = conversation.id;
    ConversationRepository.getConversation({id})
      .then(response => response?.data && setConversation(current => current?.id === id ? {...current, ...response.data} : current))
      .catch(() => {
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectionToken]);

  useEffect(() => {
    if (!conversation?.messageId || messages.length === 0) return;
    const timeout = setTimeout(() => document.getElementById(`message-${conversation.messageId}`)
      ?.scrollIntoView({behavior: "smooth", block: "center"}), 150);
    return () => clearTimeout(timeout);
  }, [conversation?.messageId, messages]);

  useEffect(() => {
    try {
      setShowDebug(JSON.parse(localStorage.getItem("showDebug")) || false);
    } catch (e) {
      setShowDebug(false);
    }
  }, [conversation?.id]);

  useEffect(() => {
    setPrev(null);
    setNext(null);
    if (!conversation?.id || navigation) return;
    ConversationRepository.getPrevAndNext({id: conversation.id, flow: conversation.flowUid})
      .then(response => {
        setPrev(response?.data?.prev || null);
        setNext(response?.data?.next || null);
      })
      .catch(() => {
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);

  // the user's VDN from the project members (the flow's vdnManager is no longer updated)
  const memberVdn = members.find(member => member.username === currentUser?.username)?.vdn;
  const agentVdn = memberVdn || flow?.configuration?.redirect_vdn;
  // a user with their own VDN whose browser phone is not Connected for this project hands over / calls by VDN instead
  const callByVdnFallback = Boolean(memberVdn) && !webrtc.isConnected(project?.id);
  const callByBrowserPhone = useBrowserPhone && !callByVdnFallback;

  const handoverToVdn = () => ConversationRepository.handoverToAgent({id: conversation?.id, username: currentUser?.username, vdn: agentVdn})
    .then(response => {
      setConversation(current => response?.data || current);
      toast.success("Successfully handed over to agent");
    })
    .catch(() => toast.error("Failed to hand over to agent"));

  // to this browser for browser phone roles (the conversation becomes HANDOVER, pushed over the websocket), to the
  // VDN otherwise
  const handleHandover = () => {
    if (!callByBrowserPhone) return handoverToVdn();
    return webrtc.takeOverCall({projectId: project?.id, conversationId: conversation?.id});
  };

  const callCustomerByVdn = async () => {
    if (!agentVdn || !conversation?.sender) {
      toast.info("No VDN configured for you in this project");
      return;
    }
    if (!(await confirmCall({title: `Do you want to initiate conversation with ${conversation.sender}`}))) return;
    const body = {
      username: currentUser?.username,
      vdn: agentVdn,
      phone_number: conversation.sender,
      initiate_call_api: flow?.configuration?.initiate_call_api,
    };
    ConversationRepository.initiateConversation({body, params: {conversation_id: conversation.id, flow_uid: flowUid}})
      .then(response => toast.success(response?.data?.message || `Initiated conversation with ${body.phone_number}`))
      .catch(error => toast.error(error?.message || `Failed to initiate conversation with ${body.phone_number}`));
  };

  const callCustomer = async () => {
    if (callByBrowserPhone) {
      if (!(await confirmCall({title: `Do you want to call ${conversation?.sender || "the customer"} from your browser`}))) return;
      return webrtc.callCustomer({
        projectId: project?.id, conversationId: conversation?.id, phoneNumber: conversation?.sender,
      });
    }
    return callCustomerByVdn();
  };

  const monitorConversation = async () => {
    if (conversation?.monitoredBy && conversation?.monitoredBy !== currentUser?.username) {
      if (!(await confirmMonitoring())) return;
    }
    ConversationRepository.monitorConversation({id: conversation?.id, username: currentUser?.username, flowUid})
      .then(response => {
        setConversation(current => response?.data || current);
        toast.success("You are now monitoring this conversation");
      })
      .catch(() => console.log("Failed to monitor conversation"));
  };

  const isMonitoredByMe = conversation?.monitoredBy && conversation.monitoredBy === currentUser?.username;

  useEffect(() => {
    const canAutoSupervise = [ProjectRoles.AGENT, ProjectRoles.MANAGER, ProjectRoles.ADMIN].includes(role) && Boolean(memberVdn);
    if (showSupervise && canAutoSupervise && conversation?.id && conversation.status === "ACTIVE"
      && conversation.type !== "CHAT_BOT" && !conversation.monitoredBy) {
      monitorConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, conversation?.status, conversation?.monitoredBy, showSupervise, role, memberVdn]);

  const name = conversation?.senderName || flow?.phonebook?.[conversation?.sender] || conversation?.sender;
  const initials = initialsOf(name);
  const avatar = AVATAR_COLORS[(Number(conversation?.id) || 0) % AVATAR_COLORS.length];
  const language = conversation ? subProjectOf?.(conversation) : null;
  const tags = useMemo(() => (conversation ? tagsOf?.(conversation) || [] : []), [conversation, tagsOf]);

  const messageTools = (
    <>
      <Tooltip title={showDebug ? "Hide debug messages" : "Show debug messages"}>
        <IconButton
          sx={{...roundButtonSx, ...(showDebug ? {color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)"} : {})}}
          onClick={() => setShowDebug(current => {
            localStorage.setItem("showDebug", JSON.stringify(!current));
            return !current;
          })}
        >
          <HugeiconsIcon icon={Bug01Icon} size={16}/>
        </IconButton>
      </Tooltip>
      <FlowProtectionButton/>
    </>
  );

  const actionButtons = (
    <>
      {showSupervise && (
        <Button
          onClick={monitorConversation}
          startIcon={<HugeiconsIcon icon={EyeIcon} size={16}/>}
          sx={{...tintedActionSx, color: "#374151", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"}}}
        >
          {isMonitoredByMe ? "Supervising" : "Supervise"}
        </Button>
      )}
      {conversation?.type !== "CHAT_BOT" && conversation?.status !== "ACTIVE" && (
        <Button
          onClick={callCustomer}
          startIcon={<HugeiconsIcon icon={PhoneIcon} size={16}/>}
          sx={{...tintedActionSx, ...softGreenSx, display: {xs: "none", md: "inline-flex"}}}
        >
          Call customer
        </Button>
      )}
      {conversation?.status === "ACTIVE" && useBrowserPhone && (
        <SendToGroupButton
          projectId={project?.id} conversationId={conversation?.id}
          sx={{...tintedActionSx, color: "#374151", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"}}}
        />
      )}
      {conversation?.status === "ACTIVE" && (
        <Button
          onClick={handleHandover}
          startIcon={<HugeiconsIcon icon={callByBrowserPhone ? HeadsetIcon : CustomerSupportIcon} size={16}/>}
          sx={{...tintedActionSx, color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}}}
        >
          {callByBrowserPhone ? "Take over call" : "Handover"}
        </Button>
      )}
    </>
  );

  return (
    <>
      <ContactPhoneNumber
        conversation={conversation}
        open={openContact}
        handleClose={() => setOpenContact(false)}
        onSaved={(saved) => setConversation(current => current ? {...current, senderName: saved?.senderName} : current)}
      />
      <Modal open={open} onClose={() => setConversation(null)} aria-labelledby={"conversation-modal-title"}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: {xs: "100%", md: "min(1100px, 92vw)"}, height: {xs: "100%", md: "86vh"},
          display: "flex", flexDirection: "column", overflow: "hidden", outline: "none",
          bgcolor: "#fff", borderRadius: {xs: 0, md: "20px"}, boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)",
        }}>
          {/* Header */}
          <Box sx={{px: {xs: 2.5, md: 3}, pt: {xs: 1.5, md: 2.5}, pb: {xs: 1.25, md: 2}}}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"} flexWrap={"wrap"}
                 columnGap={{xs: 1, md: 2}} rowGap={1.5}>
            <Stack direction={"row"} alignItems={"center"} gap={1.5} sx={{minWidth: 0, flex: 1, order: 1}}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 15, fontWeight: 700, bgcolor: avatar.bg, color: avatar.color,
              }}>
                {initials || <HugeiconsIcon icon={CustomerSupportIcon} size={20}/>}
              </Box>
              <Box sx={{minWidth: 0}}>
                <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
                  <SenderName name={name} sender={conversation?.sender}
                              sx={{
                                fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em",
                                maxWidth: {xs: 200, md: 280},
                              }}/>
                  <TransferNumber conversation={conversation} size={"medium"}/>
                  <Tooltip title={conversation?.senderName ? "Edit contact" : "Add as contact"}>
                    <IconButton size={"small"} onClick={() => setOpenContact(true)} sx={{color: "#8A8AA0"}}>
                      <HugeiconsIcon icon={UserEdit01Icon} size={16}/>
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"} sx={{mt: 0.25}}>
                  <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>#{conversation?.id}</Typography>
                  {conversation?.senderName && conversation?.sender && (
                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                      <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>·</Typography>
                      <SenderName name={conversation.sender} sender={conversation.sender}
                                  sx={{fontSize: 12, color: "#8A8AA0"}}/>
                    </Stack>
                  )}
                  {conversation && <StatusBadge conversation={conversation}/>}
                </Stack>
              </Box>
            </Stack>

            <Stack direction={"row"} alignItems={"center"} gap={1}
                   sx={{flexShrink: 0, order: 2, display: {xs: "none", md: "flex"}}}>
              {actionButtons}
              {messageTools}
            </Stack>
            <Tooltip title={"Close"}>
              <IconButton aria-label={"Close"} sx={{
                ...roundButtonSx, border: 0, width: 38, height: 38, color: "#23264D", bgcolor: "#F2F2F7",
                "&:hover": {bgcolor: "#E6E6EF"}, flexShrink: 0, order: {xs: 2, md: 3}, ml: {md: -1},
              }}
                          onClick={() => setConversation(null)}>
                <HugeiconsIcon icon={Cancel01Icon} size={20}/>
              </IconButton>
            </Tooltip>
          </Stack>

          {(showSupervise || conversation?.status === "ACTIVE") && (
          <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} gap={1} flexWrap={"wrap"}
                 sx={{mt: 2, display: {xs: "flex", md: "none"}}}>
            {actionButtons}
          </Stack>
          )}
          </Box>

          {/* Info strip */}
          <Box sx={{
            mx: {xs: 2.5, md: 3}, mb: {xs: 1.25, md: 2}, borderRadius: "14px", bgcolor: "#FAFAFD", border: "1px solid #F0F0F6",
          }}>
            <ButtonBase
              onClick={() => setInfoOpen(!infoExpanded)}
              aria-expanded={infoExpanded}
              aria-label={infoExpanded ? "Hide details" : "Show details"}
              sx={{width: "100%", justifyContent: "space-between", px: {xs: 1.25, md: 1.5}, py: 0.75, borderRadius: "14px"}}
            >
              <Typography sx={{fontSize: 13, fontWeight: 600, color: "#4A4A63"}}>Details about the conversation</Typography>
              <Box sx={{
                width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                color: "#6B6B80", bgcolor: "#F2F2F7", transform: infoExpanded ? "rotate(180deg)" : "none",
                transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}>
                <HugeiconsIcon icon={ArrowDown01Icon} size={16}/>
              </Box>
            </ButtonBase>
            <Collapse in={infoExpanded}>
              <Box sx={{
                px: {xs: 1, md: 1.5}, pb: {xs: 1, md: 1.5}, pt: 0.5, display: "grid", gap: 2,
                gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))"},
              }}>
                <InfoItem icon={Calendar03Icon} label={"STARTED"}>
                  {conversation?.dateCreated ? moment(conversation.dateCreated).format("DD MMM YYYY, HH:mm") : "—"}
                </InfoItem>
                <InfoItem icon={LanguageSkillIcon} label={"LANGUAGE"}>
                  {language?.languageName || language?.languageCode || conversation?.language || "—"}
                </InfoItem>
                <InfoItem icon={ArrowDataTransferHorizontalIcon} label={"SUPERVISED BY"}>
                  {conversation?.monitoredBy || "Nobody"}
                </InfoItem>
                <InfoItem icon={Tag01Icon} label={"TAGS"}>
                  <TagPills names={tags}/>
                </InfoItem>
              </Box>
            </Collapse>
          </Box>

          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}
                 sx={{display: {xs: "flex", md: "none"}, px: 2.5, py: 0.75}}>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8A8AA0"}}>MESSAGES</Typography>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              {conversation?.type !== "CHAT_BOT" && conversation?.status !== "ACTIVE" && (
                <Tooltip title={"Call customer"}>
                  <IconButton onClick={callCustomer} aria-label={"Call customer"} sx={{
                    ...roundButtonSx, border: 0, ...softGreenSx,
                  }}>
                    <HugeiconsIcon icon={PhoneIcon} size={16}/>
                  </IconButton>
                </Tooltip>
              )}
              {messageTools}
            </Stack>
          </Stack>

          {/* Messages */}
          <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", bgcolor: "#FAFAFD", borderTop: "1px solid #F0F0F6", px: {xs: 2.5, md: 3}, py: 2}}>
            {messagesLoading && messages.length === 0 ? (
              <Stack gap={2} sx={{minHeight: 240}}>
                {[0.6, 0.4, 0.7, 0.5].map((width, index) => (
                  <Skeleton key={index} variant={"rounded"} height={48}
                            sx={{width: `${width * 100}%`, borderRadius: "14px", alignSelf: index % 2 ? "flex-end" : "flex-start"}}/>
                ))}
              </Stack>
            ) : conversation && (
              <SupervisionHistoryItem
                conversation={conversation}
                messages={messages}
                project={flow}
                loadDuration={true}
                showUserTalking={true}
                showDebug={showDebug}
                playerSlot={playerSlot}
                updateConversation={(changes) => setConversation(current => ({...current, ...changes}))}
                fetchMessages={fetchMessages}
              />
            )}
          </Box>

          <Stack direction={"row"} alignItems={"center"} gap={1} sx={{flexShrink: 0, px: {xs: 2.5, md: 3}, py: {xs: 1, md: 1.5}}}>
            <IconButton
              aria-label={"Previous conversation"}
              disabled={!prevItem}
              onClick={() => setConversation(prevItem)}
              sx={previousButtonSx}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16}/>
              Previous
            </IconButton>
            <Box ref={setPlayerSlot} sx={{flex: 1, minWidth: 0}}/>
            <IconButton
              aria-label={"Next conversation"}
              disabled={!nextItem}
              onClick={() => setConversation(nextItem)}
              sx={nextButtonSx}
            >
              Next
              <HugeiconsIcon icon={ArrowRight01Icon} size={16}/>
            </IconButton>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};
